import { createElement as h, useState, type ReactNode } from "react";
import type { GlbExportOptions, QualityMode, SceneState } from "../../../shared/api-contract";
import { sc } from "../inspector/bindings";
import { CompactCheck, CompactNumber, CompactSelect, CompactSlider, CompactText, MiniBtn, Row } from "../inspector/rows";
import type { BindingCtx, DomainRead } from "../../../state/editor/types";
import type { Binding, InertTag, SectionDef } from "../inspector/schema";
import { downloadBlob, downloadJSON, pickFile } from "../../../util/download";
import { pngWithDpi, tiffDpiTags } from "../../../util/imageMeta";
import { blobToImageData, drawWatermark } from "../../../util/rasterize";
import { convertGlb, MESH_FORMAT_LABEL, type MeshFormat } from "../../../util/meshExport";
import { useEditorStore, useUi } from "../../../state/editor/useEditor";
import { uiBinding } from "../inspector/bindings";
import { useExportSize } from "../useExportSize";
import { S3Control, ShareLinkControl } from "./custom/PluginControls";
import { useCaps } from "../CapsProvider";
import { useViewer } from "../../../state/ViewerProvider";
import { captureHost } from "../scene/sceneFile";
import { useSceneFileActions } from "../scene/useSceneFileActions";
import gifWorkerUrl from "gif.js/dist/gif.worker.js?url";

/** Row helper — `Row` requires `children` in props, so pass it explicitly. */
const row = (label: string, ...children: ReactNode[]) =>
  h(Row, { label, children: children.length === 1 ? children[0] : children });

/** Same, for a row the dead-control audit flagged. */
const inertRow = (label: string, inert: InertTag, ...children: ReactNode[]) =>
  h(Row, { label, inert, children: children.length === 1 ? children[0] : children });

const CRF_INERT: InertTag = {
  reason:
    "Unreachable from here. VideoExporter falls back to crf only when `bitrate` is null, and this panel always sends a parsed bitrate. Change `bitrate` above instead.",
};
const GLB_UNREAD: InertTag = {
  reason: "Stored, never read. Viewer.exportGLB does not consult this option; the produced .glb is byte-identical either way.",
};

/**
 * Export tab — the data-driven port of the old ExportMenu plus the full iJewel
 * export surface (Asset Export GLB, Video Export with FFmpeg fields, Image
 * Export, S3 Connection, Share Link). Render quality / pixel ratio ride the
 * scene slice via `sc`; everything else is a custom row firing an RPC directly
 * (blobs/JSON come back over the wire and are saved through the download
 * helpers). Field labels/defaults are transcribed verbatim from the iJewel
 * schema. JSX-free (`h`) to match the other section modules.
 */

/**
 * File-dialog import for a `kind:"button"` row.
 *
 * `pickFile`'s promise reports only whether a file was chosen — it settles its
 * callback with `.finally`, so a rejection inside `work` is swallowed there and
 * surfaces as an unhandled rejection instead. Carrying the failure back across
 * the dialog boundary and rethrowing it is what makes `ButtonRowView` render it
 * inline in ed-red; without it a malformed .vjson or a rejected import RPC left
 * the row looking like the import had succeeded.
 */
async function importFile(accept: string, work: (file: File) => Promise<void>): Promise<void> {
  let failure: unknown;
  await pickFile(accept, async (f) => {
    try {
      await work(f);
    } catch (e) {
      failure = e;
    }
  });
  if (failure !== undefined) throw failure instanceof Error ? failure : new Error(String(failure));
}

/**
 * Labels quote the ratio the engine actually applies (Engine.setQuality:
 * low → 1, interactive → min(dpr, 2), high → min(dpr, 3), ultra → dpr × 1.5).
 * The old "Medium (2x) / High (3x) / Ultra (device max)" text was wrong on
 * every display: "High (3x)" renders at 2x on a standard 2-DPR screen, and
 * "device max" actually oversamples 50% beyond the device ratio.
 */
const QUALITY_OPTIONS: { id: QualityMode; label: string }[] = [
  { id: "low", label: "Low (1x)" },
  { id: "interactive", label: "Medium (up to 2x)" },
  { id: "high", label: "High (up to 3x)" },
  { id: "ultra", label: "Ultra (1.5× device)" },
];

/**
 * Quality is not only a label: Engine.setQuality also reassigns the base pixel
 * ratio. `sc` would patch just `quality`, leaving the "Custom Pixel Ratio"
 * slider immediately below this row displaying the pre-change number while the
 * engine renders at the new one — and the next drag of that slider would push
 * the stale value back. Re-read the scene state after the call so the two
 * adjacent rows always agree with what is on screen.
 */
const qualityBinding: Binding<string> = {
  domain: "scene",
  get: (read: DomainRead) => (read("scene") as SceneState).quality,
  set: (v: string) => ({
    patch: { quality: v as QualityMode } as Partial<SceneState>,
    call: (api) => api.setQuality(v as QualityMode).then(() => api.getSceneState()),
    reconcile: (res) => res,
  }),
};

/** Image Export — filename, progressive frames, tiled super-res, crop, png/jpeg/webp. */
type ImgFormat = "png" | "jpeg" | "webp" | "tiff";

function ImageExportRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const caps = useCaps();
  const [ui] = useUi();
  const [filename, setFilename] = useState("snapshot");
  // Follows RENDERER's Custom size; 1600 square only while none is set.
  const { w, h: h2, setW, setH, following } = useExportSize(1600, 1600);
  const [transparent, setTransparent] = useState(false);
  // Print resolution written INTO the file. The machinery already existed
  // (util/imageMeta) but for a long time only the retired Canvas export panel
  // offered it, so there was nowhere in this editor to set DPI at all.
  const [dpi, setDpi] = useState(300);
  const [frames, setFrames] = useState(64);
  const [rows, setRows] = useState(1);
  const [cols, setCols] = useState(1);
  const [cropOn, setCropOn] = useState(false);
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [cropW, setCropW] = useState(800);
  const [cropH, setCropH] = useState(800);
  // Per-format, not one shared flag: only the clicked button may spin and the
  // siblings must be locked out — two snapshots racing over the same frame
  // buffer produce a garbled capture. `error` exists because grab() was a
  // try/finally with no catch behind `void grab(...)`: a rejected snapshot
  // downloaded nothing, said nothing, and left the button back at idle.
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Past the GPU renderbuffer cap a single capture fails; tiling is the answer
  // and the user should not have to do the arithmetic to find that out.
  const [autoTile, setAutoTile] = useState(true);
  const grab = async (format: ImgFormat, ext: string) => {
    if (pending) return;
    setPending(format);
    setError(null);
    try {
      const crop = cropOn ? { x: cropX, y: cropY, width: cropW, height: cropH } : undefined;
      const autoX = autoTile ? Math.ceil(w / 2048) : 1;
      const autoY = autoTile ? Math.ceil(h2 / 2048) : 1;
      const [tilesX, tilesY] = [Math.max(cols, autoX), Math.max(rows, autoY)];
      const tiled = tilesX > 1 || tilesY > 1;
      // TIFF has no snapshot format of its own — capture PNG and re-encode.
      const shotFormat = format === "tiff" ? "png" : format;
      // v31: snapshot() gained frames/crop directly, so a single (untiled)
      // capture no longer needs to be routed through snapshotTiled just to
      // get a crop. Pre-v31 viewers don't support those fields on snapshot(),
      // so keep routing crop through snapshotTiled(1x1) for them.
      let blob = tiled
        ? await ctx.ring.snapshotTiled({ width: w, height: h2, tilesX, tilesY, framesPerTile: frames, format: shotFormat, transparent, crop })
        : caps.v31
          ? await ctx.ring.snapshot({ width: w, height: h2, transparent, format: shotFormat, frames, crop })
          : crop
            ? await ctx.ring.snapshotTiled({ width: w, height: h2, tilesX: 1, tilesY: 1, framesPerTile: frames, format: shotFormat, transparent, crop })
            : await ctx.ring.snapshot({ width: w, height: h2, transparent, format: shotFormat });

      // Watermark BEFORE any metadata: it re-encodes through a 2D canvas, which
      // would drop a pHYs chunk written first.
      if (ui.watermark.on && ui.watermark.text) {
        blob = await drawWatermark(blob, ui.watermark.text, `image/${shotFormat}`, ui.watermark.light);
      }

      if (format === "tiff") {
        const { data, width, height } = await blobToImageData(blob);
        const UTIF = (await import("utif")).default;
        const tiff = UTIF.encodeImage(data.buffer, width, height, tiffDpiTags(dpi)) as ArrayBuffer;
        downloadBlob(new Blob([tiff], { type: "image/tiff" }), `${filename}_${w}x${h2}_${dpi}dpi.tif`);
        return;
      }
      // PNG and TIFF are the only formats that carry a resolution tag; JPEG and
      // WebP have nowhere to put it, so the value only names the file there.
      const tagged = format === "png" ? await pngWithDpi(blob, dpi) : blob;
      downloadBlob(tagged, `${filename}_${w}x${h2}_${dpi}dpi.${ext}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Snapshot failed.");
    } finally { setPending(null); }
  };
  /** One download button, spinning only while its own format is encoding. */
  const grabBtn = (format: ImgFormat, ext: string, icon: string, label: string, primary = false) =>
    h(MiniBtn, {
      key: format,
      icon,
      primary,
      busy: pending === format,
      disabled: pending !== null && pending !== format,
      onClick: () => void grab(format, ext),
      children: pending === format ? "Rendering…" : label,
    });
  const numRow = (label: string, v: number, set: (n: number) => void, min: number, step = 1) =>
    row(label, h(CompactNumber, { value: v, min, step, width: 70, onChange: set }));
  return h(
    "div",
    { className: "flex flex-col" },
    row("Filename", h(CompactText, { value: filename, onChange: setFilename })),
    row("Size",
      h(CompactNumber, { key: "w", value: w, min: 1, step: 1, width: 64, onChange: setW }),
      h("span", { key: "x", className: "text-[12px] text-ed-text-3" }, "×"),
      h(CompactNumber, { key: "h", value: h2, min: 1, step: 1, width: 64, onChange: setH }),
    ),
    row("Transparent", h(CompactCheck, { checked: transparent, onChange: setTransparent })),
    numRow("DPI (print)", dpi, setDpi, 1),
    row("", h("span", { className: "block px-3 pb-1 text-[11px] leading-snug text-ed-text-3" },
      "Written into a .png so print software reads the right size. .jpeg / .webp carry no resolution tag, so there it only names the file."
      + (following ? " Size is following the Renderer section — type above to override." : ""))),
    numRow("Frame Count", frames, setFrames, 1),
    row("Auto tile", h(CompactCheck, { checked: autoTile, onChange: setAutoTile })),
    row("", h("span", { className: "block px-3 pb-1 text-[11px] leading-snug text-ed-text-3" },
      "Anything over 2048px is captured in tiles — a single capture that large exceeds the GPU renderbuffer and fails. Rows/columns below raise the tile count further; they never lower it.")),
    numRow("Tile Rows", rows, setRows, 1),
    numRow("Tile Columns", cols, setCols, 1),
    row("Crop Rect", h(CompactCheck, { checked: cropOn, onChange: setCropOn })),
    cropOn
      ? row(
          "(x, y, w, h)",
          h(CompactNumber, { key: "cx", value: cropX, min: 0, step: 1, width: 56, onChange: setCropX }),
          h(CompactNumber, { key: "cy", value: cropY, min: 0, step: 1, width: 56, onChange: setCropY }),
          h(CompactNumber, { key: "cw", value: cropW, min: 1, step: 1, width: 56, onChange: setCropW }),
          h(CompactNumber, { key: "ch", value: cropH, min: 1, step: 1, width: 56, onChange: setCropH }),
        )
      : null,
    h("div", { className: "flex flex-wrap gap-1.5 px-3 py-1.5" },
      grabBtn("png", "png", "download", "Download .png", true),
      grabBtn("jpeg", "jpg", "image", "Download .jpeg"),
      grabBtn("webp", "webp", "image", "Download .webp"),
      grabBtn("tiff", "tif", "image", "Download .tiff"),
    ),
    ui.watermark.on && ui.watermark.text
      ? h("div", { className: "px-3 pb-2 text-[11px] leading-snug text-ed-text-3" },
          `Watermarked “${ui.watermark.text}”. Video is not watermarked — it is muxed inside the viewer.`)
      : null,
    error ? h("div", { className: "px-3 pb-2 text-[11px] text-ed-red" }, error) : null,
  );
}

/** Video Export — turntable / animation recording with FFmpeg-path options. */
function VideoExportRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const [fps, setFps] = useState(30);
  const [convergeMode, setConverge] = useState(true);
  const [bitrate, setBitrate] = useState("10M");
  const [crf, setCrf] = useState(17);
  const [angle, setAngle] = useState(360);
  const [seconds, setSeconds] = useState(5);
  const [ccw, setCcw] = useState(false);
  const [endPad, setEndPad] = useState(500);
  const [mp4, setMp4] = useState(true);
  // Keyed by BUTTON id, not by operation: four of the five encode buttons share
  // record()/recordViews(), so a per-operation flag lit the spinner on the wrong
  // button (and the original single `busy` was read by only one of the five).
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  const parseBitrate = (s: string): number => {
    const m = /^([\d.]+)\s*([kKmMgG]?)/.exec(s.trim());
    if (!m) return 10_000_000;
    const n = parseFloat(m[1]);
    const mult = { k: 1e3, m: 1e6, g: 1e9, "": 1 }[m[2].toLowerCase()] ?? 1;
    return Math.round(n * mult);
  };
  /**
   * Runs one encode under its button's pending key. The catch is the point:
   * these calls were fired as `void record()` with a try/finally and no catch,
   * so a rejection (no WebCodecs support, no saved camera views, muxer failure)
   * became an unhandled promise rejection — no file, no message, button back at
   * idle as if it had worked.
   */
  const run = async (id: string, work: () => Promise<void>) => {
    if (pending) return;
    setPending(id);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Recording failed.");
    } finally {
      setPending(null);
    }
  };
  /**
   * Spin the camera 360°, snapshot each frame, encode a real GIF with gif.js.
   * Ported from the Canvas chrome — the only place it existed.
   *
   * GIF is palette-limited and every frame here is a full render, so the long
   * side is capped at 512 and the frame count at 72: 12s x 60fps would be 720
   * renders for a file nobody wants.
   */
  const recordGif = async () => {
    const N = Math.max(8, Math.min(72, Math.round(seconds * fps)));
    const params = await ctx.ring.getRendererParams();
    const ow = params.width || 1280;
    const oh = params.height || 720;
    const scale = Math.min(1, 512 / Math.max(ow, oh));
    const gw = Math.max(1, Math.round(ow * scale));
    const gh = Math.max(1, Math.round(oh * scale));
    const GIF = (await import("gif.js/dist/gif.js")).default;
    const gif = new GIF({ workers: 2, quality: 10, width: gw, height: gh, workerScript: gifWorkerUrl });
    const delay = Math.max(20, Math.round((seconds * 1000) / N));
    const [p0, t] = await Promise.all([ctx.ring.getCameraPosition(), ctx.ring.getCameraTarget()]);
    const rad = Math.hypot(p0.x - t.x, p0.z - t.z);
    const a0 = Math.atan2(p0.z - t.z, p0.x - t.x);
    try {
      for (let i = 0; i < N; i++) {
        const a = a0 + (ccw ? -1 : 1) * (i / N) * Math.PI * 2;
        ctx.ring.setCameraPosition(t.x + rad * Math.cos(a), p0.y, t.z + rad * Math.sin(a));
        gif.addFrame(await blobToImageData(await ctx.ring.snapshot({ width: gw, height: gh, format: "png" })), { delay, copy: true });
      }
    } finally {
      // Restore the live camera whatever happened — an aborted GIF used to
      // leave the user looking at wherever the sweep stopped.
      ctx.ring.setCameraPosition(p0.x, p0.y, p0.z);
    }
    const blob = await new Promise<Blob>((resolve) => { gif.on("finished", resolve); gif.render(); });
    downloadBlob(blob, "turntable.gif");
  };

  const record = async () => {
    const blob = await ctx.ring.exportTurntableVideo({
      fps, durationMs: seconds * 1000, format: mp4 ? "mp4" : "webm",
      bitrate: parseBitrate(bitrate), convergeMode, crf,
      turntableAngle: angle, turntableCCW: ccw, endPaddingMs: endPad,
    });
    downloadBlob(blob, mp4 ? "turntable.mp4" : "turntable.webm");
  };
  /**
   * Record the loaded GLTF clip.
   *
   * This button was tagged "mislabelled" on a claim that turned out to be
   * false: exportTurntableVideo DOES drive the clip — Viewer passes the
   * GltfAnimationController into the exporter, which calls beginExternalDrive()
   * and advances it one frame per captured frame. Three things were actually
   * wrong, and only the third had been noticed:
   *
   *  1. it swept the camera, so a clip was recorded while orbiting;
   *  2. it used the TURNTABLE duration, not the clip's;
   *  3. ⚠️ beginExternalDrive() returns `this.playing` and advance() no-ops
   *     unless playing — so with the clip paused it silently recorded a static
   *     model for the full duration and reported success.
   */
  const recordGltfAnim = async () => {
    const st = await ctx.ring.getGltfPlaybackState();
    if (!st.available || st.duration <= 0) {
      throw new Error("This model has no GLTF animation to record.");
    }
    // Fix (3): start the clip rather than recording stillness and calling it a
    // GLTF export.
    if (!st.playing) await ctx.ring.playGltfAnimation();
    const blob = await ctx.ring.exportTurntableVideo({
      fps,
      // Fix (2): the clip's own length.
      durationMs: Math.round(st.duration * 1000),
      format: mp4 ? "mp4" : "webm",
      bitrate: parseBitrate(bitrate), convergeMode, crf,
      // Fix (1): turntableAngle overrides `revolutions` in VideoExporter, so 0
      // holds the camera perfectly still and only the clip moves.
      turntableAngle: 0,
      endPaddingMs: endPad,
    });
    downloadBlob(blob, mp4 ? "gltf-anim.mp4" : "gltf-anim.webm");
  };
  const recordViews = async () => {
    const blob = await ctx.ring.recordAllCameraViews({ fps, durationMs: seconds * 1000, format: mp4 ? "mp4" : "webm", bitrate: parseBitrate(bitrate) });
    downloadBlob(blob, mp4 ? "camera-views.mp4" : "camera-views.webm");
  };
  // Free-run recorder (checklist "Start Recording" — the live canvas records
  // until Stop, unlike the deterministic frame-stepped turntable capture above).
  const toggleRecording = async () => {
    setError(null);
    setPending("live");
    try {
      if (recording) {
        const blob = await ctx.ring.stopRecording();
        downloadBlob(blob, mp4 ? "recording.mp4" : "recording.webm");
        // Flip only after the muxed blob is in hand: the old code flipped first,
        // so a failed stop left the UI offering "Start Recording" for a recorder
        // the viewer was still running.
        setRecording(false);
      } else {
        await ctx.ring.startRecording({ fps, bitrate: parseBitrate(bitrate) });
        // Same reasoning inverted — never offer "Stop" for a recorder that
        // never started (the call was previously not even awaited).
        setRecording(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : recording ? "Could not stop the recording." : "Could not start recording.");
    } finally {
      setPending(null);
    }
  };
  /**
   * One encode button: spins on its own id, locked out while any other encode
   * runs — and while the free-run recorder is live, since the frame-stepped
   * capture drives the camera and would be captured into that recording.
   */
  const vidBtn = (id: string, icon: string, label: string, work: () => Promise<void>, primary = false, inert?: InertTag) =>
    h(MiniBtn, {
      key: id,
      icon,
      primary,
      inert,
      busy: pending === id,
      disabled: recording || (pending !== null && pending !== id),
      onClick: () => void run(id, work),
      children: pending === id ? "Encoding…" : label,
    });
  const txt = (label: string, v: string, set: (s: string) => void) =>
    row(label, h(CompactText, { value: v, onChange: set }));
  const num = (label: string, v: number, set: (n: number) => void, min = 0, step = 1) =>
    row(label, h(CompactNumber, { value: v, min, step, width: 70, onChange: set }));
  const inum = (label: string, inert: InertTag, v: number, set: (n: number) => void, min = 0, step = 1) =>
    inertRow(label, inert, h(CompactNumber, { value: v, min, step, width: 70, onChange: set }));
  return h(
    "div",
    { className: "flex flex-col" },
    row("Frame Rate", h(CompactNumber, { value: fps, min: 1, max: 60, step: 1, width: 60, onChange: setFps })),
    row("Converge mode", h(CompactCheck, { checked: convergeMode, onChange: setConverge })),
    txt("bitrate", bitrate, setBitrate),
    inum("crf", CRF_INERT, crf, setCrf, 0, 1),
    num("Turntable animation angle", angle, setAngle, 0, 5),
    num("Turntable animation time (sec)", seconds, setSeconds, 1, 1),
    row("Turntable animation CCW", h(CompactCheck, { checked: ccw, onChange: setCcw })),
    num("After recording wait time (ms)", endPad, setEndPad, 0, 50),
    row("MP4 (else WebM)", h(CompactCheck, { checked: mp4, onChange: setMp4 })),
    h("div", { className: "flex flex-wrap gap-1.5 px-3 py-1.5" },
      h(MiniBtn, {
        icon: recording ? "stop_circle" : "fiber_manual_record",
        primary: !recording,
        active: recording,
        busy: pending === "live",
        disabled: pending !== null && pending !== "live",
        onClick: () => void toggleRecording(),
        children: recording ? "Stop Recording" : "Start Recording",
      }),
    ),
    h("div", { className: "flex flex-wrap gap-1.5 px-3 py-1.5" },
      vidBtn("turntable", "movie", "Record turntable animation", record, true),
      vidBtn("gif", "gif_box", "Record .gif", recordGif),
      vidBtn("views", "videocam", "Record Camera Views", recordViews),
      vidBtn("gltfAnim", "animation", "Record GLTF Anim", recordGltfAnim),
    ),
    error ? h("div", { className: "px-3 pb-1 text-[11px] text-ed-red" }, error) : null,
  );
}

/** Asset Export (GLB) — full iJewel AssetExporter field set. */
function GlbExportRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const caps = useCaps();
  const { client } = useViewer();
  const store = useEditorStore();
  const [o, setO] = useState({
    name: "scene", compress: false, encodeSpeed: 5,
    quantizePosition: 14, quantizeNormal: 10, quantizeColor: 8, quantizeTexcoord: 12, quantizeGeneric: 12,
    viewerConfig: true, embedImagePreviews: false, onlyVisible: false,
    encrypt: false, encryptKey: "", encodeUint16Rgbe: false, convertMeshToIndexed: false,
    ignoreInvalidMorphTargetTracks: true, ignoreInvalidTextures: true,
  });
  const [busy, setBusy] = useState(false);
  // The viewer exports GLB and only GLB; the other three are re-serialised
  // client-side from it (util/meshExport), so they need no RPC and no contract
  // change. Ported from the Canvas chrome, the only place they existed.
  const [fmt, setFmt] = useState<MeshFormat>("glb");
  // exportGLB rejects for real (encryption without a key, a Draco encode that
  // runs out of memory); it used to be fired as `void exportGlb()` inside a
  // catch-less try/finally, so the failure was invisible and no .glb appeared.
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<typeof o>) => setO((s) => ({ ...s, ...patch }));
  const exportGlb = async () => {
    setBusy(true);
    setError(null);
    try {
      // `pluginIncludeList` is deliberately not sent: the contract marks it
      // informational and Viewer.exportGLB never reads it, so the 16 chips that
      // used to build it changed nothing in the .glb.
      const opts: GlbExportOptions = { ...o };
      // v65: "Scene Settings" carries the WHOLE scene (of-scene/2, every file
      // packed inside), which the viewer reads back when the .glb is opened —
      // the old `viewerConfig` copy was written and never read by anything.
      // The viewer refuses a scene inside an encrypted file (nothing could
      // decrypt it on load), so encryption keeps the old behaviour; and a
      // conversion to another format would only throw the scene away.
      if (caps.sceneFileV65 && o.viewerConfig && !o.encrypt && fmt === "glb") {
        opts.embedScene = true;
        opts.sceneHost = captureHost(client, store);
      }
      const glb = await ctx.ring.exportGLB(opts);
      const { blob, filename } = await convertGlb(glb, fmt, o.name || "scene");
      downloadBlob(blob, filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : "GLB export failed.");
    } finally { setBusy(false); }
  };
  const chk = (label: string, key: keyof typeof o) =>
    row(label, h(CompactCheck, { checked: o[key] as boolean, onChange: (v: boolean) => set({ [key]: v } as Partial<typeof o>) }));
  const ichk = (label: string, key: keyof typeof o, inert: InertTag) =>
    inertRow(label, inert, h(CompactCheck, { checked: o[key] as boolean, onChange: (v: boolean) => set({ [key]: v } as Partial<typeof o>) }));
  const quant = (label: string, key: keyof typeof o) =>
    row(label, h(CompactSlider, { value: o[key] as number, min: 1, max: 16, step: 1, onChange: (v: number) => set({ [key]: v } as Partial<typeof o>) }));
  return h(
    "div",
    { className: "flex flex-col" },
    row("(name)", h(CompactText, { value: o.name, onChange: (v: string) => set({ name: v }) })),
    row("Format", h(CompactSelect, {
      value: fmt,
      options: (Object.keys(MESH_FORMAT_LABEL) as MeshFormat[]).map((k) => ({ id: k, label: MESH_FORMAT_LABEL[k] })),
      onChange: (v: string) => setFmt(v as MeshFormat),
    })),
    fmt !== "glb"
      ? row("", h("span", { className: "block px-3 pb-1 text-[11px] leading-snug text-ed-text-3" },
          "Converted from the exported GLB in the browser. The Draco and encryption options below apply to that intermediate GLB, not to the converted file."))
      : null,
    chk("DRACO Compress", "compress"),
    o.compress ? row("Encode Speed", h(CompactSlider, { value: o.encodeSpeed, min: 1, max: 10, step: 1, onChange: (v: number) => set({ encodeSpeed: v }) })) : null,
    o.compress ? quant("POSITION", "quantizePosition") : null,
    o.compress ? quant("NORMAL", "quantizeNormal") : null,
    o.compress ? quant("COLOR", "quantizeColor") : null,
    o.compress ? quant("TEX_COORD", "quantizeTexcoord") : null,
    o.compress ? quant("GENERIC", "quantizeGeneric") : null,
    chk("Scene Settings", "viewerConfig"),
    ichk("Embed Image Previews", "embedImagePreviews", GLB_UNREAD),
    chk("Only Visible", "onlyVisible"),
    chk("Encrypt", "encrypt"),
    o.encrypt ? row("Encrypt Password", h(CompactText, { value: o.encryptKey, onChange: (v: string) => set({ encryptKey: v }) })) : null,
    ichk("Compress hdr env maps", "encodeUint16Rgbe", GLB_UNREAD),
    chk("Convert to indexed", "convertMeshToIndexed"),
    ichk("Ignore invalid animations", "ignoreInvalidMorphTargetTracks", GLB_UNREAD),
    chk("Ignore invalid textures", "ignoreInvalidTextures"),
    h("div", { className: "px-3 py-1.5" },
      h(MiniBtn, { icon: "deployed_code", primary: true, busy, onClick: () => void exportGlb(), children: busy ? "Exporting…" : "Export GLB" }),
    ),
    error ? h("div", { className: "px-3 pb-1 text-[11px] text-ed-red" }, error) : null,
    h("div", { className: "px-3 pb-2 text-[11px] text-ed-text-3" },
      "Encryption / image previews / indexed conversion / per-plugin include-list are stored & round-trip; geometry, Draco quant, only-visible and metadata embedding are live."),
  );
}

/**
 * Whole scene (v65) — the same three actions as Basic › Export: one `.jscene`
 * with everything except the ring (every file packed inside), opened back as
 * one Undo step, and the ring as a `.glb` with that scene inside. Import also
 * opens the two older formats (.vjson, settings JSON): the viewer upgrades
 * them on the way in. Their own Export/Import rows were removed at the user's
 * request (2026-10-08). On an older viewer the methods do not exist and RPC
 * has no timeout, so the row only says why it is empty.
 */
function WholeSceneRows() {
  const a = useSceneFileActions();
  if (!a.available) {
    return h("span", { className: "block px-3 py-1.5 text-[11px] text-ed-text-3" }, "Saving the whole scene needs viewer contract v65 or newer.");
  }
  const other = (key: string) => !!a.busy && a.busy !== key;
  return h(
    "div",
    { className: "flex flex-col" },
    row(
      "Whole scene (.jscene)",
      h("div", { className: "flex flex-wrap justify-end gap-1.5" },
        h(MiniBtn, { icon: "save", primary: true, busy: a.busy === "save", disabled: other("save"), onClick: () => void a.save(), children: "Export" }),
        h(MiniBtn, { icon: "folder_open", busy: a.busy === "open", disabled: other("open"), onClick: () => void a.open(), children: "Import" }),
      ),
    ),
    row(
      "Ring + scene (.glb)",
      h(MiniBtn, {
        icon: "deployed_code",
        busy: a.busy === "ring",
        disabled: !a.hasModel || other("ring"),
        onClick: () => void a.ringWithScene(),
        children: "Export",
      }),
    ),
    h("div", { className: "px-3 pb-1.5 text-[11px] leading-snug text-ed-text-3" },
      "Lights, environments, metals, stones, camera, effects, configurator, watermark and On Body — every file packed inside. Import replaces the scene exactly, as one Undo step; it also opens a Ring + scene .glb's setup onto this ring, and older .vjson and settings files."),
  );
}

/** Watermark rows. `uiBinding` keeps them pure RowDefs over the `ui` slice, so
 *  the on-screen overlay and the export compositor cannot disagree — which is
 *  exactly what went wrong when the watermark was DOM chrome only. */
const wmOn: Binding<boolean> = uiBinding(
  (ui) => (ui.watermark as { on: boolean }).on,
  (v, ui) => ({ watermark: { ...(ui.watermark as object), on: v } }),
);
const wmText: Binding<string> = uiBinding(
  (ui) => (ui.watermark as { text: string }).text,
  (v, ui) => ({ watermark: { ...(ui.watermark as object), text: v } }),
);
const wmLight: Binding<boolean> = uiBinding(
  (ui) => (ui.watermark as { light: boolean }).light,
  (v, ui) => ({ watermark: { ...(ui.watermark as object), light: v } }),
);

export const exportSections: SectionDef[] = [
  {
    id: "branding",
    title: "Branding",
    defaultCollapsed: true,
    domains: ["ui"],
    rows: [
      { id: "wmOn", kind: "checkbox", label: "Show watermark", binding: wmOn },
      { id: "wmText", kind: "text", label: "Watermark text", binding: wmText },
      { id: "wmLight", kind: "checkbox", label: "Light watermark", binding: wmLight },
      {
        id: "wmNote",
        kind: "note",
        label: "",
        text:
          "Drawn on the canvas AND composited into exported stills. Video is exempt — MP4/WebM are muxed inside the viewer, out of reach from here. Use the light variant on dark backdrops.",
      },
    ],
  },
  {
    id: "assetExport",
    title: "Asset Export (GLB)",
    rows: [{ id: "glb", kind: "custom", label: "GLB", component: GlbExportRow }],
  },
  {
    id: "video",
    title: "Video Export",
    defaultCollapsed: true,
    rows: [{ id: "videoRow", kind: "custom", label: "Video", component: VideoExportRow }],
  },
  {
    id: "imageExport",
    title: "Image Export",
    defaultCollapsed: true,
    rows: [{ id: "image", kind: "custom", label: "Image", component: ImageExportRow }],
  },
  {
    id: "s3",
    // Title is one of the 69 the parity guard matches by exact string, so it
    // stays — but the section is no longer a credential form that uploads
    // nothing. It exports to workspace storage, which IS the customer's own
    // bucket whenever they have connected one under Settings → Storage.
    title: "S3 Connection",
    defaultCollapsed: true,
    info:
      "Sends an export to your storage and gives you back a URL. Connect your own AWS bucket under Settings → Storage and uploads land there instead — this editor never handles your keys.",
    rows: [{ id: "s3", kind: "custom", label: "", component: () => h(S3Control) }],
  },
  {
    id: "shareLink",
    title: "Share Link",
    defaultCollapsed: true,
    // The "best-effort" badge came off with the rewrite: the link is a real
    // hosted embed now (server/modules/embeds), not a truncated fragment.
    rows: [{ id: "share", kind: "custom", label: "", component: () => h(ShareLinkControl) }],
  },
  // ─── Extras below: not in the iJewel Export checklist. ───
  {
    id: "renderQuality",
    title: "Render Quality",
    defaultCollapsed: true,
    rows: [
      {
        id: "quality",
        kind: "dropdown",
        label: "Quality",
        options: QUALITY_OPTIONS,
        binding: qualityBinding,
      },
      {
        id: "pixelRatio",
        kind: "slider",
        label: "Custom Pixel Ratio",
        min: 0.5,
        max: 4,
        step: 0.25,
        binding: sc(
          (s) => s.pixelRatio,
          (v) => ({ pixelRatio: v }),
          (r, v) => r.setPixelRatio(v),
        ),
      },
    ],
  },
  {
    id: "sceneSettings",
    title: "Scene Settings",
    defaultCollapsed: true,
    rows: [
      {
        id: "wholeScene",
        kind: "custom",
        label: "",
        keywords: "whole scene jscene save open import export ring + scene glb embed scene file older vjson settings json",
        component: WholeSceneRows,
      },
    ],
  },
  {
    id: "metalMaterial",
    title: "Metal Material",
    defaultCollapsed: true,
    visible: (_read, ctx) => !!ctx.selectionId,
    rows: [
      {
        id: "metal",
        kind: "button",
        label: "Selected metal",
        buttons: [
          {
            label: "Export",
            icon: "file_download",
            run: async (ctx) => {
              const id = ctx.selectionId;
              if (!id) throw new Error("Select a part first.");
              const json = await ctx.ring.exportMetalJSON(id);
              // The section is shown for ANY selection, but exportMetalJSON
              // resolves null for a non-metal group. The old bare `return` made
              // the button a completely silent no-op on a gem selection.
              if (!json) throw new Error("The selected part has no metal material.");
              downloadJSON(json, "metal-material.json");
            },
          },
          {
            label: "Import",
            icon: "file_upload",
            run: (ctx) => {
              const id = ctx.selectionId;
              if (!id) throw new Error("Select a part first.");
              return importFile(".json", async (f) => { await ctx.ring.importMetalJSON(id, await f.text()); });
            },
          },
        ],
      },
    ],
  },
  {
    id: "diamondMaterial",
    title: "Diamond Material",
    defaultCollapsed: true,
    visible: (_read, ctx) => !!ctx.selectionId,
    rows: [
      {
        id: "diamond",
        kind: "button",
        label: "Selected gem",
        buttons: [
          {
            label: "Export",
            icon: "file_download",
            run: async (ctx) => {
              const id = ctx.selectionId;
              if (!id) throw new Error("Select a part first.");
              const json = await ctx.ring.exportDiamondJSON(id);
              // Mirror of the metal case: null means "the selected group is not
              // a gem", which used to produce no file and no explanation.
              if (!json) throw new Error("The selected part has no diamond material.");
              downloadJSON(json, "diamond-material.json");
            },
          },
          {
            label: "Import",
            icon: "file_upload",
            run: (ctx) => {
              const id = ctx.selectionId;
              if (!id) throw new Error("Select a part first.");
              return importFile(".json", async (f) => { await ctx.ring.importDiamondJSON(id, await f.text()); });
            },
          },
        ],
      },
    ],
  },
];
